import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { mediaSrc } from "../lib/media";
import { formatDate } from "../lib/i18n";
import Seo from "../components/Seo";
import ShareButton from "../components/ShareButton";
import DemoBadge from "../components/DemoBadge";
import { MediaTile, PersonCard, EmptyState, Loader } from "../components/Blocks";

export default function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/events/${slug}`).then(({ data }) => setEvent(data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <EmptyState message="Événement introuvable." testid="event-not-found" />;
  if (!event) return <Loader />;

  return (
    <div data-testid="event-detail-page">
      <Seo
        title={event.name}
        description={(event.description || "").slice(0, 180)}
        path={`/evenements/${event.slug}`}
        image={event.hero_media_url || (event.media || [])[0]?.url}
        kind="event"
        type="article"
      />
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        {event.hero_media_url && (
          <img src={mediaSrc(event.hero_media_url)} alt={event.name} className="absolute inset-0 w-full h-full object-cover opacity-75" />
        )}
        <div className="absolute inset-0 vb-scrim" />
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 pb-16 w-full">
          <div className="flex items-center gap-3 mb-4">
            <span className="vb-label !text-[#FF2E63]">{event.event_type}</span>
            <DemoBadge show={event.is_demo} />
          </div>
          <h1 className="vb-display text-4xl sm:text-5xl lg:text-6xl font-black" data-testid="event-title">
            {event.name}
          </h1>
          <p className="mt-4 text-sm text-neutral-300">
            {formatDate(event.date_time)} · {event.venue} · {event.location}
            {event.capacity ? ` · ${event.capacity} places` : ""}
          </p>
          <div className="mt-6">
            <ShareButton
              path={`/evenements/${event.slug}`}
              title={`${event.name} — VytaliBoots`}
              text="Découvrez cet événement sur VytaliBoots."
              testid="event-share-button"
            />
          </div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <p className="text-base text-neutral-300 leading-relaxed max-w-3xl">{event.description}</p>
          <div>
            <div className="vb-label mb-6">Galerie</div>
            {(event.media || []).length === 0 ? (
              <EmptyState message="Aucun média publié pour cet événement." testid="event-media-empty" />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4" data-testid="event-media-grid">
                {event.media.map((m, i) => (
                  <MediaTile key={m.id} item={m} testid={`event-media-${i}`} className="aspect-square" />
                ))}
              </div>
            )}
          </div>
          {(event.people || []).length > 0 && (
            <div>
              <div className="vb-label mb-6">Créateurs & Ambassadeurs</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="event-people-grid">
                {event.people.map((p, i) => (
                  <PersonCard key={p.id} person={p} testid={`event-person-${i}`} />
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-8">
          {event.partner && (
            <div className="vb-card p-6">
              <div className="vb-label mb-3">Lieu partenaire</div>
              <div className="vb-display text-xl">{event.partner.business_name}</div>
              <p className="mt-2 text-sm text-neutral-400">{event.partner.short_description}</p>
              <Link to={`/partenaires/${event.partner.slug}`} data-testid="event-partner-link" className="vb-btn vb-btn-ghost mt-6 w-full">
                Voir le partenaire
              </Link>
            </div>
          )}
          {(event.collaborators || []).length > 0 && (
            <div className="vb-card p-6">
              <div className="vb-label mb-3">Collaborateurs</div>
              {event.collaborators.map((c) => (
                <div key={c.id} className="text-sm text-neutral-300 flex items-center gap-2 py-1">
                  {c.name} <span className="text-neutral-600">· {c.collaborator_type}</span>
                  <DemoBadge show={c.is_demo} />
                </div>
              ))}
            </div>
          )}
          <div className="vb-card p-6">
            <div className="vb-label mb-3">Participation</div>
            <p className="text-sm text-neutral-400">
              {event.participation_open ? "Participation ouverte à la communauté Vytali." : "Participation fermée."}
            </p>
            <Link to="/rejoindre?interet=event_participation" data-testid="event-join-link" className="vb-btn vb-btn-primary mt-6 w-full">
              Rejoindre VytaliBoots
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
